import React from "react";

export type PillVariant =
  | "blue"
  | "green"
  | "purple"
  | "orange"
  | "red"
  | "yellow"
  | "pink"
  | "gray"
  | "emerald"
  | "amber"
  | "rose"
  | "indigo"
  | "teal"
  | "lime";

export type PillSize = "xs" | "sm" | "md" | "lg";

interface PillProps {
  children: React.ReactNode;
  variant?: PillVariant;
  size?: PillSize;
  className?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
}

const Pill: React.FC<PillProps> = ({
  children,
  variant = "gray",
  size = "sm",
  className = "",
  onClick,
  icon,
}) => {
  const getVariantClasses = (variant: PillVariant): string => {
    const variants = {
      blue: "bg-blue-200/80 border-blue-300",
      green: "bg-green-200/80 border-green-300",
      purple: "bg-purple-200/80 border-purple-300",
      orange: "bg-orange-200/80 border-orange-300",
      red: "bg-red-200/80 border-red-300",
      yellow: "bg-yellow-200/80 border-yellow-300",
      pink: "bg-pink-200/80 border-pink-300",
      gray: "bg-gray-200/80 border-gray-300",
      emerald: "bg-emerald-200/80 border-emerald-300",
      amber: "bg-amber-200/80 border-amber-300",
      rose: "bg-rose-200/80 border-rose-300",
      indigo: "bg-indigo-200/80 border-indigo-300",
      teal: "bg-teal-200/80 border-teal-300",
      lime: "bg-lime-200/80 border-lime-300",
    };
    return variants[variant];
  };

  const getSizeClasses = (size: PillSize): string => {
    const sizes = {
      xs: "px-2 py-0.5 text-xs",
      sm: "px-3 py-1 text-xs",
      md: "px-4 py-1.5 text-sm",
      lg: "px-5 py-2 text-base",
    };
    return sizes[size];
  };

  const baseClasses =
    " justify-center inline-flex items-center gap-1 rounded-full font-medium backdrop-blur-sm border transition-all duration-200 hover:scale-105 text-pill-black";
  const variantClasses = getVariantClasses(variant);
  const sizeClasses = getSizeClasses(size);
  const clickableClasses = onClick ? "cursor-pointer hover:shadow-md" : "";

  return (
    <span
      className={`${baseClasses} ${variantClasses} ${sizeClasses} ${clickableClasses} ${className}`}
      onClick={onClick}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </span>
  );
};

export default Pill;
